import { useCallback } from 'react';
import { atom, useAtom } from 'jotai';
import type { ColorPaletteName } from '../../constants/color';
import { includes } from '../../utils/array';
import { useMapVisibility } from './useMapVisibility';
import { useLegendConfig } from './useLegendConfig';
import { useMapColor } from './useMapColor';
import { useMapColorPalette } from './useMapColorPalette';

type AggregationType = (typeof AGGREGATION_TYPES)[number];

type DimensionalAggregationType = Extract<
  AggregationType,
  'areas' | 'area_pref' | 'grid_heatmap' | 'heatmap'
>;

type NonDimensionalAggregationType = Exclude<
  AggregationType,
  DimensionalAggregationType
>;

type AggregationDimension = (typeof AGGREGATION_DIMENSIONS)[number];

type AggregationMetric = (typeof AGGREGATION_METRICS)[number];

type AggregationConfig = Readonly<
  | {
      type: DimensionalAggregationType;
      dimension: AggregationDimension;
      metric: AggregationMetric;
    }
  | {
      type: NonDimensionalAggregationType;
      dimension?: undefined;
      metric?: undefined;
    }
>;

type UseAggregationConfig = {
  aggregationConfig: AggregationConfig | null;
  startAggregation: (config: AggregationConfig) => void;
  clearAggregation: () => void;
  getAvailableAggregationTypes: (
    dimension: AggregationDimension | undefined,
    metric: AggregationMetric | undefined
  ) => DimensionalAggregationType[];
};

const AGGREGATION_TYPES = [
  'areas',
  'area_pref',
  'grid_heatmap',
  'heatmap',
  'arcs',
  'od_map',
  'links',
] as const;

const AGGREGATION_DIMENSIONS = ['end', 'start', 'home', 'work'] as const;

const AGGREGATION_METRICS = [
  'count_per_area',
  'count',
  'average_traveled_distance',
  'average_traveled_time',
  'average_activity_time',
] as const;

const DEFAULT_COLOR_PALETTES = {
  areas: 'vivid',
  area_pref: 'vivid',
  grid_heatmap: 'vivid',
  heatmap: 'vivid',
  arcs: 'cool',
  links: 'colorful',
  od_map: 'vivid',
} as const satisfies Record<AggregationType, ColorPaletteName>;

const getAvailableAggregationTypes = (
  dimension: AggregationDimension | undefined,
  metric: AggregationMetric | undefined
): DimensionalAggregationType[] => {
  if (
    includes(
      [
        'average_traveled_distance',
        'average_traveled_time',
        'average_activity_time',
      ],
      metric
    )
  ) {
    return includes(['home', 'work'], dimension)
      ? ['areas']
      : ['areas', 'grid_heatmap'];
  }
  if (includes(['count_per_area', 'count'], metric)) {
    return dimension === 'home'
      ? ['areas', 'area_pref', 'grid_heatmap', 'heatmap']
      : ['areas', 'grid_heatmap', 'heatmap'];
  }
  return [];
};

const aggregationConfigAtom = atom<AggregationConfig | null>(null);

const useAggregationConfig = (): UseAggregationConfig => {
  const [aggregationConfig, setAggregationConfig] = useAtom(
    aggregationConfigAtom
  );

  const {
    visibleAreaType,
    visibleLinkTypes,
    setVisibleAreaType,
    setVisibleLinkTypes,
    setIsAggregationVisible,
  } = useMapVisibility();
  const { mapColor } = useMapColor();
  const { setMapColorPalette, setMapColorOpacity } = useMapColorPalette();
  const { resetLegendConfig } = useLegendConfig();

  const startAggregation = useCallback(
    (config: AggregationConfig): void => {
      if (
        config.dimension &&
        !includes(
          getAvailableAggregationTypes(config.dimension, config.metric),
          config.type
        )
      ) {
        return;
      }

      setAggregationConfig(config);

      // 可視化対象に応じて初期の表示設定を行う
      if (config.type === 'areas' && !visibleAreaType) {
        setVisibleAreaType('town');
      }
      if (config.type === 'links' && visibleLinkTypes.length === 0) {
        setVisibleLinkTypes(['highway', 'railway']);
      }
      setIsAggregationVisible(true);

      // 可視化モードが変わった場合はカラーパレットを初期化する
      if (config.type !== aggregationConfig?.type) {
        setMapColorPalette(DEFAULT_COLOR_PALETTES[config.type]);
        setMapColorOpacity(mapColor === 'light' ? 0.5 : 0.7);
      }

      // 凡例設定を初期化する
      resetLegendConfig();
    },
    [
      aggregationConfig,
      visibleAreaType,
      visibleLinkTypes,
      mapColor,
      setAggregationConfig,
      setVisibleAreaType,
      setVisibleLinkTypes,
      setIsAggregationVisible,
      setMapColorPalette,
      setMapColorOpacity,
      resetLegendConfig,
    ]
  );

  const clearAggregation = useCallback((): void => {
    setAggregationConfig(null);
  }, [setAggregationConfig]);

  return {
    aggregationConfig,
    startAggregation,
    clearAggregation,
    getAvailableAggregationTypes,
  };
};

export type {
  AggregationDimension,
  DimensionalAggregationType,
  AggregationMetric,
  AggregationType,
};
export {
  useAggregationConfig,
  AGGREGATION_DIMENSIONS,
  AGGREGATION_METRICS,
  AGGREGATION_TYPES,
};
